import { useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { format, isSameDay } from 'date-fns';
import { useChatStore } from '../store/chat-store';
import type { SystemEvent } from '../store/chat-store';
import { Avatar } from './Avatar';
import { AudioBubble } from './AudioBubble';
import { colorsForUser } from '../lib/avatar';
import type { ChatMessage } from '@contracts/messages';

type Row =
  | { kind: 'separator'; key: string; label: string }
  | { kind: 'message'; key: string; msg: ChatMessage; showHeader: boolean; showTail: boolean; mine: boolean }
  | { kind: 'system'; key: string; event: SystemEvent; mine: boolean };

function dayLabel(d: Date): string {
  const today = new Date();
  if (isSameDay(d, today)) return 'Today';
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (isSameDay(d, yesterday)) return 'Yesterday';
  return format(d, 'EEEE, d MMM');
}

function timeLabel(iso: string): string {
  return format(new Date(iso), 'HH:mm');
}

const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

export function MessageList({
  currentUserId,
  onReact,
  serverUrl,
}: {
  currentUserId: string;
  onReact: (messageId: string, emoji: string) => void;
  serverUrl: string;
}) {
  const messages = useChatStore(s => s.messages);
  const systemEvents = useChatStore(s => s.systemEvents);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);
  const [showJump, setShowJump] = useState(false);
  // Count of messages from other users that arrived while scrolled up.
  const [unread, setUnread] = useState(0);
  const prevMsgCountRef = useRef(messages.length);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const highlightTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Scroll to a specific message (used when a reply quote is clicked) and
  // flash it briefly so the eye can find it.
  const jumpToMessage = (id: string) => {
    const root = scrollerRef.current;
    if (!root) return;
    const el = root.querySelector<HTMLElement>(`[data-mid="${CSS.escape(id)}"]`);
    if (!el) return; // original may be older than the loaded window
    stickToBottomRef.current = false;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightId(id);
    if (highlightTimer.current) clearTimeout(highlightTimer.current);
    highlightTimer.current = setTimeout(() => setHighlightId(null), 1600);
  };

  useEffect(() => () => { if (highlightTimer.current) clearTimeout(highlightTimer.current); }, []);

  const rows = useMemo<Row[]>(() => {
    // Merge messages and system events by timestamp.
    type TimelineItem =
      | { type: 'msg'; at: number; msg: ChatMessage }
      | { type: 'sys'; at: number; event: SystemEvent };

    const items: TimelineItem[] = [
      ...messages.map(msg => ({ type: 'msg' as const, at: new Date(msg.createdAt).getTime(), msg })),
      ...systemEvents.map(ev => ({ type: 'sys' as const, at: new Date(ev.at).getTime(), event: ev })),
    ];
    items.sort((a, b) => a.at - b.at);

    const out: Row[] = [];
    let lastDay: Date | null = null;
    let lastMsgUserId: string | null = null;
    let lastMsgTime: number | null = null;

    items.forEach((item, i) => {
      const at = new Date(item.at);
      if (!lastDay || !isSameDay(lastDay, at)) {
        out.push({ kind: 'separator', key: `sep-${item.type}-${i}-${item.at}`, label: dayLabel(at) });
        lastDay = at;
        lastMsgUserId = null;
        lastMsgTime = null;
      }

      if (item.type === 'sys') {
        out.push({
          kind: 'system',
          key: item.event.id,
          event: item.event,
          mine: item.event.userId === currentUserId,
        });
        // System rows break message-grouping streaks.
        lastMsgUserId = null;
        lastMsgTime = null;
        return;
      }

      const msg = item.msg;
      const sameUser = lastMsgUserId === msg.userId;
      const closeInTime = lastMsgTime != null && Math.abs(item.at - lastMsgTime) < 60_000;
      const showHeader = !sameUser || !closeInTime;

      const nextItem = items[i + 1];
      const nextIsMessage = nextItem && nextItem.type === 'msg';
      const nextSameDay = !!nextItem && isSameDay(new Date(nextItem.at), at);
      const nextSameUser = nextIsMessage && (nextItem as { msg: ChatMessage }).msg.userId === msg.userId;
      const nextCloseInTime = nextItem ? Math.abs(nextItem.at - item.at) < 60_000 : false;
      const showTail = !nextIsMessage || !nextSameDay || !nextSameUser || !nextCloseInTime;

      out.push({
        kind: 'message',
        key: msg.id,
        msg,
        showHeader,
        showTail,
        mine: msg.userId === currentUserId,
      });

      lastMsgUserId = msg.userId;
      lastMsgTime = item.at;
    });
    return out;
  }, [messages, systemEvents, currentUserId]);

  // Track stick-to-bottom + jump button. Wired as an onScroll prop (below)
  // rather than addEventListener in an effect: the scroller is absent on the
  // empty-state render, so a one-time effect would attach to nothing and the
  // listener would never get wired once messages arrive.
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = distance < 80;
    setShowJump(distance > 200);
    if (distance < 80) setUnread(0); // back at bottom → caught up
  };

  // Auto-scroll on new messages when already at bottom.
  useEffect(() => {
    const el = scrollerRef.current;
    if (el && stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [rows.length]);

  // Count newly-arrived messages from other users while scrolled up.
  useEffect(() => {
    const prev = prevMsgCountRef.current;
    prevMsgCountRef.current = messages.length;
    const added = messages.length - prev;
    if (added > 0 && !stickToBottomRef.current) {
      const fromOthers = messages.slice(prev).filter(m => m.userId !== currentUserId).length;
      if (fromOthers > 0) setUnread(u => u + fromOthers);
    }
  }, [messages, currentUserId]);

  const jumpToBottom = () => {
    const el = scrollerRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    setUnread(0);
  };

  if (messages.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center px-6 py-10">
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center mb-3">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#4338CA" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        </div>
        <div className="text-sm font-semibold text-gray-700">No messages yet</div>
        <div className="text-xs text-gray-500 mt-1">Be the first to say hi 👋</div>
      </div>
    );
  }

  return (
    <div className="relative flex-1 min-h-0">
      <div
        ref={scrollerRef}
        onScroll={handleScroll}
        className="absolute inset-0 overflow-y-auto scroll-smoothy px-3 py-2 space-y-0.5"
      >
        {rows.map(row => {
          if (row.kind === 'separator') {
            return <DaySeparator key={row.key} label={row.label} />;
          }
          if (row.kind === 'system') {
            return (
              <div key={row.key} className="msg-in">
                <SystemRow event={row.event} mine={row.mine} />
              </div>
            );
          }
          return (
            <div
              key={row.key}
              data-mid={row.msg.id}
              className={`msg-in ${highlightId === row.msg.id ? 'msg-flash' : ''}`}
            >
              <MessageRow
                msg={row.msg}
                mine={row.mine}
                showHeader={row.showHeader}
                showTail={row.showTail}
                currentUserId={currentUserId}
                onReact={onReact}
                onJumpTo={jumpToMessage}
                serverUrl={serverUrl}
              />
            </div>
          );
        })}
        <div className="h-1" />
      </div>

      {(showJump || unread > 0) && (
        <button
          type="button"
          onClick={jumpToBottom}
          className={`absolute bottom-3 right-3 shadow-md border transition rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 ${
            unread > 0
              ? 'bg-blue-600 border-blue-600 text-white hover:bg-blue-700'
              : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
          }`}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M6 9l6 6 6-6"/>
          </svg>
          {unread > 0
            ? `${unread > 9 ? '9+' : unread} new message${unread === 1 ? '' : 's'}`
            : 'Jump to latest'}
        </button>
      )}
    </div>
  );
}

function DaySeparator({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center my-2 select-none">
      <div className="px-3 py-0.5 text-[11px] font-semibold tracking-wide text-gray-500 bg-gray-100 rounded-full">
        {label}
      </div>
    </div>
  );
}

function SystemRow({ event, mine }: { event: SystemEvent; mine: boolean }) {
  const userColors = colorsForUser(event.userId);
  const isJoin = event.kind === 'join';
  const subject = mine ? 'You' : event.name;
  const verb = isJoin
    ? mine ? 'joined the chat' : 'joined'
    : mine ? 'left the chat' : 'left';

  return (
    <div className="flex justify-center items-center my-1.5 select-none">
      <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-gray-50 border border-gray-100 text-[11px] text-gray-600 max-w-[92%]">
        <span
          className="inline-flex items-center justify-center w-4 h-4 rounded-full"
          style={{ background: userColors.bg, color: userColors.fg }}
          aria-hidden="true"
        >
          {isJoin ? (
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
              <polyline points="10 17 15 12 10 7"/>
              <line x1="15" y1="12" x2="3" y2="12"/>
            </svg>
          ) : (
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
          )}
        </span>
        <span>
          <span className="font-semibold text-gray-700">{subject}</span> {verb}
        </span>
        <span className="text-gray-400">·</span>
        <span className="tabular-nums">{timeLabel(event.at)}</span>
      </div>
    </div>
  );
}

function MessageRow({
  msg,
  mine,
  showHeader,
  showTail,
  currentUserId,
  onReact,
  onJumpTo,
  serverUrl,
}: {
  msg: ChatMessage;
  mine: boolean;
  showHeader: boolean;
  showTail: boolean;
  currentUserId: string;
  onReact: (messageId: string, emoji: string) => void;
  onJumpTo: (messageId: string) => void;
  serverUrl: string;
}) {
  const safe = DOMPurify.sanitize(msg.text);
  const userColors = colorsForUser(msg.userId);
  const setReplyTarget = useChatStore(s => s.setReplyTarget);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!pickerOpen) return;
    // composedPath() for shadow-DOM-safe outside-click detection.
    const onDoc = (e: MouseEvent) => {
      const path = e.composedPath ? e.composedPath() : [];
      if (pickerRef.current && !path.includes(pickerRef.current)) setPickerOpen(false);
    };
    const t = setTimeout(() => document.addEventListener('mousedown', onDoc), 0);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', onDoc); };
  }, [pickerOpen]);

  const bubbleRadius = mine
    ? `18px 18px ${showTail ? '4px' : '18px'} 18px`
    : `18px 18px 18px ${showTail ? '4px' : '18px'}`;

  const reactionEntries = msg.reactions ? Object.entries(msg.reactions).filter(([, u]) => u.length > 0) : [];

  return (
    <div className={`group flex items-end gap-2 ${mine ? 'flex-row-reverse' : ''} ${showHeader ? 'mt-2' : ''}`}>
      <div className="w-9 shrink-0">
        {!mine && showTail && <Avatar userId={msg.userId} name={msg.userName} size={32} />}
      </div>

      <div className={`flex flex-col max-w-[78%] ${mine ? 'items-end' : 'items-start'}`}>
        {!mine && showHeader && (
          <div className="text-[11px] font-semibold mb-0.5 px-1" style={{ color: userColors.fg }}>
            {msg.userName}
          </div>
        )}

        <div className={`relative flex items-center gap-1 ${mine ? 'flex-row-reverse' : ''}`}>
          <div
            className={`px-3 py-2 text-sm break-words shadow-sm ${
              mine
                ? 'vip-btn-primary bg-gradient-to-br from-blue-600 to-indigo-600 text-white'
                : 'bg-white text-gray-900 border border-gray-100'
            }`}
            style={{ borderRadius: bubbleRadius, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {msg.replyTo && (
              <button
                type="button"
                onClick={() => msg.replyTo && onJumpTo(msg.replyTo.id)}
                title="Go to replied message"
                className={`block w-full text-left mb-1.5 rounded-md px-2 py-1 text-[11px] border-l-2 transition cursor-pointer ${
                  mine ? 'bg-white/15 border-white/50 hover:bg-white/25' : 'bg-gray-50 border-gray-300 hover:bg-gray-100'
                }`}
              >
                <div className={`font-semibold ${mine ? 'text-white/90' : 'text-gray-600'}`}>
                  {msg.replyTo.userName}
                </div>
                <div className={`${mine ? 'text-white/70' : 'text-gray-500'} truncate`}>
                  {msg.replyTo.text}
                </div>
              </button>
            )}
            {msg.media?.type === 'audio' && (
              <div className={msg.text ? 'mb-1.5' : ''}>
                <AudioBubble
                  url={msg.media.url}
                  serverUrl={serverUrl}
                  durationMs={msg.media.durationMs}
                  mine={mine}
                />
              </div>
            )}
            {msg.text && <span dangerouslySetInnerHTML={{ __html: safe }} />}
          </div>

          {/* Hover toolbar: Reply + React. Hidden until the row is hovered. */}
          <div
            ref={pickerRef}
            className="relative opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition flex items-center gap-0.5"
          >
            <button
              type="button"
              aria-label="Reply"
              title="Reply"
              onClick={() => setReplyTarget(msg)}
              className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="9 17 4 12 9 7" />
                <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
              </svg>
            </button>
            <button
              type="button"
              aria-label="React"
              title="React"
              onClick={() => setPickerOpen(v => !v)}
              className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                <line x1="9" y1="9" x2="9.01" y2="9" />
                <line x1="15" y1="9" x2="15.01" y2="9" />
              </svg>
            </button>
            {pickerOpen && (
              <div className={`absolute bottom-9 z-20 flex gap-1 bg-white rounded-full shadow-xl ring-1 ring-black/10 px-2 py-1.5 ${mine ? 'right-0' : 'left-0'}`}>
                {QUICK_REACTIONS.map(e => (
                  <button
                    key={e}
                    type="button"
                    onClick={() => { onReact(msg.id, e); setPickerOpen(false); }}
                    className="text-lg leading-none hover:scale-125 transition"
                  >
                    {e}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Reaction chips */}
        {reactionEntries.length > 0 && (
          <div className={`flex flex-wrap gap-1 mt-1 ${mine ? 'justify-end' : 'justify-start'}`}>
            {reactionEntries.map(([emoji, users]) => {
              const reacted = users.includes(currentUserId);
              return (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => onReact(msg.id, emoji)}
                  className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs border transition ${
                    reacted
                      ? 'bg-blue-50 border-blue-300 text-blue-700'
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                  title={users.length === 1 ? '1 reaction' : `${users.length} reactions`}
                >
                  <span>{emoji}</span>
                  <span className="tabular-nums">{users.length}</span>
                </button>
              );
            })}
          </div>
        )}

        {showTail && (
          <div className={`text-[10px] mt-0.5 px-1 ${mine ? 'text-blue-700/70' : 'text-gray-400'}`}>
            {timeLabel(msg.createdAt)}
          </div>
        )}
      </div>
    </div>
  );
}
